import { EditOutlined } from '@mui/icons-material';
import { Button, Divider, ListItemText, Menu, MenuItem } from '@mui/material';
import {
  gridColumnDefinitionsSelector,
  selectedGridRowsCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { cloneElement, isValidElement, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { BulkEditGridColDef } from '../bulk-edit-data-grid.types';
import { useBulkEditDataGridContext } from '../bulk-edit-data-grid/bulk-edit-data-grid.context';
import BulkEditDrawer from '../bulk-edit-drawer/bulk-edit-drawer.component';
import { useBulkEditDrawer } from '../bulk-edit-drawer/bulk-edit-drawer.hooks';
import { ToolbarProps } from '../inc-data-grid.component';

const GridToolbarBulkEditButton = ({
  bulkEdit,
}: {
  bulkEdit: ToolbarProps['bulkEdit'];
}) => {
  const { t } = useTranslation();

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'IncDataGrid.BulkEdit.Menu',
  });

  const api = useGridApiContext();
  const bulkEditDrawer = useBulkEditDrawer();
  const columns: BulkEditGridColDef[] = useGridSelector(
    api,
    gridColumnDefinitionsSelector,
  );
  const selectedRowCount = useGridSelector(api, selectedGridRowsCountSelector);
  const { firstSelectedGridRowData } = useBulkEditDataGridContext();
  const firstSelectedGridRowDataValue = firstSelectedGridRowData?.value;

  return (
    <>
      <Button
        color="primary"
        variant="contained"
        size="small"
        startIcon={<EditOutlined />}
        disabled={selectedRowCount === 0}
        data-testid="IncDataGrid.BulkEditButton"
        {...bindTrigger(popupState)}
      >
        {t('IncDataGrid.BulkEditButton.label')}
      </Button>
      <Menu {...bindMenu(popupState)}>
        {columns
          .filter(column =>
            Object.keys(bulkEdit?.fields)?.includes(column.field),
          )
          .map(column => (
            <MenuItem
              key={column.field}
              onClick={() => {
                let formValues;
                // Honors the column's valueGetter, for fields that aren't a
                // top-level property of the row (e.g. nested display info).
                const rowValue = api.current.getRowValue(
                  firstSelectedGridRowDataValue,
                  column,
                );

                if (column.customType === 'keyValue') {
                  const extendedAttributeValues = [];
                  Object.entries(rowValue).forEach(([key, value]) => {
                    extendedAttributeValues.push({ key, value });
                  });
                  formValues = extendedAttributeValues;
                } else {
                  formValues = rowValue;
                }

                const defaultValues = {
                  [column.field]: formValues,
                };

                bulkEditDrawer.open({
                  column,
                  bulkEdit,
                  defaultValues,
                });
                popupState.close();
              }}
            >
              <ListItemText>{column.headerName}</ListItemText>
            </MenuItem>
          ))}
        {!!bulkEdit?.additionalMenuItems && <Divider />}
        {bulkEdit?.additionalMenuItems?.map(
          (item, index) =>
            isValidElement(item) &&
            cloneElement(item as ReactElement, {
              key: index,
              onClick: e => {
                if (item.props.onClick) {
                  item.props.onClick(e);
                }
                popupState.close();
              },
            }),
        )}
      </Menu>
      <BulkEditDrawer {...bulkEditDrawer} />
    </>
  );
};
export default GridToolbarBulkEditButton;
